/* eslint-disable consistent-return */
import React, { useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Tooltip from '@material-ui/core/Tooltip';
import clsx from 'clsx';
import get from 'lodash/get';
import T from 'i18n-react';

import classes from './Select.module.scss';

const SelectInput = (props) => {
  const { selectProps = {} } = props;
  const {
    label,
    onChange,
    className,
    required,
    touched,
    errorText,
    onBlur,
    loading,
    isDisabled = false,
    isMulti = false,
    isCreate = false,
    isClearable = true,
    name,
    formProps,
    value,
    options,
    placeholder = T.translate('general.select'),
    limit = 100,
    minHeight,
    customMultipleStyles = {},
    hideOptions,
    valueIsTag,
    hideIndicator,
    classes: classesProps,
    // touchValidation = false,
    ...other
  } = selectProps;

  const slTouched = touched || get(formProps, `touched.${name}`, '');
  const slErrorText = errorText || get(formProps, `errors.${name}`, '');
  const slValue = value || get(formProps, `values.${name}`, '');
  const hasError = !!(slTouched && slErrorText);

  const [labelFocus, setLabelFocus] = useState(false);

  const selectStyles = {
    indicatorsContainer: (provided) => {
      const addProps = {};
      const hideProps = {};
      if (hideOptions) {
        addProps.display = 'none !important';
      }
      if (hideIndicator) {
        hideProps.display = 'none !important;'
      }
      return {
        ...provided,
        ...addProps,
        ' > *:first-child': {
          ...hideProps,
        }
      };
    },
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    container: (provided) => ({
      ...provided,
      overflow: 'visible',
    }),
    control: (provided, state) => ({
      ...provided,
      // eslint-disable-next-line no-nested-ternary
      borderColor: state.isFocused
        ? '#003DA5 !important'
        : hasError
        ? '#f44336 !important'
        : 'rgba(0, 0, 0, 0.23)',
      boxShadow: 'none !important',
      minHeight: minHeight || '40px',
      height: minHeight || 'max-content',
      '&:hover': {
        borderColor: 'rgba(0, 0, 0, 0.87)',
      },
      ' > div': {
        fontSize: '13px',
        fontWeight: '500',
      },
      ...classesProps?.control,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'hsl(0,0%,60%)',
    }),
    menu: (provided) => {
      const addProps = {};
      if (hideOptions) {
        addProps.display = 'none !important';
      }
      return {
        ...provided,
        zIndex: 1000,
        marginTop: '3px',
        boxShadow: '0px 2px 10px rgba(35, 43, 58, 0.25)',
        ...addProps,
      };
    },
    option: (provided, state) => {
      return {
        ...provided,
        color: '#232B3A',
        fontSize: '14px',
        backgroundColor: state.isSelected || state.isFocused ? '#EEEEEE !important' : 'transparent',
      };
    },
    multiValue: (provided) => {
      const addProps = {};
      if (valueIsTag) {
        addProps.height = '24px';
        addProps.backgroundColor = '#4E5A71';
        addProps.borderRadius = '13px';
        addProps.padding = '0px 5px 0px 2px';
        addProps.display = 'flex';
        addProps.alignItems = 'center';
        addProps['> div'] = {
          color: 'white',
        };
        addProps['> div:nth-child(2)'] = {
          background: 'white',
          borderRadius: '50%',
          padding: '0',
          height: '16px',
          cursor: 'pointer',
        };
        addProps['> div:nth-child(2) svg'] = {
          width: '16px',
          height: '16px',
          color: '#4E5A71',
        };
      }
      return {
        ...provided,
        borderRadius: '4px',
        backgroundColor: '#EBEDEF',
        ' > div': {
          paddingTop: '0',
          paddingBottom: '0',
          color: '#232B3A',
        },
        ...addProps,
        ...customMultipleStyles,
      };
    },
  };

  const renderRequired = () => {
    return required ? (
      <span>
        {label}
        <span className='required-mark'>*</span>
      </span>
    ) : (
      label
    );
  };

  const commonProps = {
    ...other,
    styles: selectStyles,
    inputId: name,
    onChange: (obj) => {
      if (formProps) {
        formProps.setFieldValue(name, obj);
      }
      if (onChange) {
        onChange(name, obj);
      }
    },
    onBlur: (e) => {
      if (formProps) {
        formProps.handleBlur(e);
      }
      if (onBlur) {
        onBlur(e);
      }
      setLabelFocus(false);
    },
    onFocus: () => {
      setLabelFocus(true);
    },
    isClearable,
    isMulti,
    isCreate,
    name,
    value: slValue,
    options: slValue && slValue.length === limit ? [] : options,
    placeholder,
    formatCreateLabel: (inputValue) => {
      return `${T.translate('general.enter')} "${inputValue}"`;
    },
    isDisabled: isDisabled || false,
  };

  return (
    <Tooltip title={get(slValue, 'label', '')} placement='top'>
      <div
        className={clsx(
          classes.selectWrapper,
          isMulti && classes.isMulti,
          isDisabled && classes.isDisabled,
          label ? '' : classes.noLabel,
          className
        )}>
        {!!label && (
          <FormLabel
            component='legend'
            className={clsx(classes.label, labelFocus && classes.focus, hasError && classes.error)}>
            {renderRequired()}
          </FormLabel>
        )}

        {isCreate ? <CreatableSelect {...commonProps} /> : <Select {...commonProps} />}
        <FormHelperText className={hasError ? classes.error : classes.errorTextHide}>
          {slErrorText}
        </FormHelperText>
      </div>
    </Tooltip>
  );
};

SelectInput.propTypes = {
  selectProps: PropTypes.object.isRequired,
};

export default SelectInput;
